import React, { useState } from "react";
import "./PriceGroupSidebar.scss";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwButton from "../../../shared/OwButton/OwButton";
import IPrice_Group from "../../../../models/IPrice_Group";
import IPrice_GroupUI from "../../../../models/IPrice_GroupUI";
import INewPrice_Group from "../../../../models/INewPrice_Group";
import INewPrice_GroupUI from "../../../../models/INewPrice_GroupUI";
import { PriceGroupRow } from "./PriceGroupRow/PriceGroupRow";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { ConfirmDeleteOverlayFn } from "../../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    priceGroups?: IPrice_Group[];
    clearData?: any;
    cancel: any;
    saveFn: any;
    deleteFn: (priceGroup: IPrice_Group) => void;
};

const PriceGroupSidebar: React.FC<Props> = (props) => {
    const { uiContext } = useAppContextNonNull();

    const [priceGroups, setPriceGroups] = useState<(IPrice_GroupUI | INewPrice_GroupUI)[]>((props.priceGroups || []) as IPrice_GroupUI[]);
    const [priceGroupToDelete, setPriceGroupToDelete] = useState<IPrice_Group | null>(null);
    const [sourceElement, setSourceElement] = useState<string | null>(null);
    const [pendingChange, setPendingChange] = useState(false);
    const [isEditingCount, setIsEditingCount] = useState(0);

    const cancel = (e: any) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        props.cancel();
    };

    const updatePriceGroup = (priceGroup: IPrice_GroupUI | INewPrice_GroupUI, index: number) => {
        const _priceGroups = [...priceGroups];

        _priceGroups[index] = priceGroup;

        setPriceGroups(_priceGroups);

        setPendingChange(true);
    };

    const deletePriceGroup = (priceGroup: IPrice_Group | INewPrice_Group, index: number) => {
        if ("guid" in priceGroup) {
            const sourceElem = `price-group-row-${index}`;
            setPriceGroupToDelete(priceGroup);
            setSourceElement(sourceElem);
        } else {
            // remove the price group from the price groups array
            const _priceGroups = [...priceGroups];
            _priceGroups.splice(index, 1);

            setPriceGroups(_priceGroups);
        }

        setPendingChange(true);
    };

    const addPriceGroup = () => {
        const priceGroup = { value: "", isNew: true, editMode: true } as INewPrice_GroupUI;

        const priceGroupArr = [priceGroup, ...priceGroups];

        setPriceGroups(priceGroupArr);
    };

    const cancelDeleteFn = () => {
        setPriceGroupToDelete(null);
        setSourceElement(null);
    };

    const commitDeleteFn = () => {
        if (priceGroupToDelete) {
            props.deleteFn(priceGroupToDelete);
            setPriceGroupToDelete(null);
        }
    };

    const savePriceGroups = () => {
        props.saveFn(priceGroups);
    };

    const isEditingFn = (editing: boolean) => {
        if (editing) {
            setIsEditingCount(isEditingCount + 1);
        } else {
            if (isEditingCount > 0) {
                setIsEditingCount(isEditingCount - 1);
            }
        }
    };

    return (
        <div className="PriceGroupSidebar d-flex flex-column p-3" style={{ height: "100vh", overflow: "hidden" }}>
            <div>
                <div className="text-end">
                    <button className="btn btn-danger btn-sm" onClick={cancel}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle leftIconName={"fad fa-dollar-sign"} title={"price_groups"} />
                <hr />
            </div>
            <div>
                <div>
                    {pendingChange ? (
                        <div className={"mb-2"}>
                            <OwLabel title={"pending_changes"} className={"text-danger"} leftIconClass={"fas fa-exclamation-triangle"} />
                        </div>
                    ) : null}
                    {isEditingCount > 0 ? (
                        <div>
                            <OwLabel title={"finish_edits"} className={"text-danger"} />
                        </div>
                    ) : null}
                </div>
                <div className={"mb-2"}>
                    <OwButton fn={addPriceGroup} title={"add"} leftIconClass={"far fa-plus"} className={"btn-warning me-2"} />
                    {pendingChange ? (
                        <OwButton fn={savePriceGroups} title={"save"} leftIconClass={"far fa-floppy-disk"} disabled={isEditingCount > 0} />
                    ) : null}
                </div>
            </div>
            <div className="PriceGroupForm position-relative pb-2 flex-grow-1" style={{ overflowY: "scroll" }}>
                <ConfirmDeleteOverlayFn
                    targetElem={sourceElement}
                    handleConfirm={commitDeleteFn}
                    handleCancel={cancelDeleteFn}
                    skipMainView={true}
                    nestedParentElem={"price-group-rows"}
                    leftAdjustment={0}
                />

                <div className={"mb-2 position-relative"} id={"price-group-rows"}>
                    {priceGroups.map((priceGroup, index: number) => {
                        return (
                            <>
                                <PriceGroupRow
                                    key={`${index}-${priceGroup.value}`}
                                    index={index}
                                    deletePriceGroup={deletePriceGroup}
                                    updatePriceGroup={updatePriceGroup}
                                    isEditing={isEditingFn}
                                    priceGroup={priceGroup}
                                />
                                {uiContext.showHiddenFeatures ? (
                                    <div className="text-muted">{"guid" in priceGroup ? priceGroup.guid : null}</div>
                                ) : null}
                            </>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PriceGroupSidebar;
